import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Tooltip } from './common/tooltip.component';
import CurrencyInfoModal from './currency-info-modal.component';
import VatInfoModal from './vat-info-modal.component';
import './invoice-preferences.scss';

interface InvoicePreferencesProps {
  preferences: {
    currency: string;
    vat?: number;  // NEW - VAT percentage
    deductionType?: string;
    showDates?: string;  // Show dates in invoice
    accommodationType?: string;
    brfOrgNumber?: string;
    apartmentHouseNo?: string;
    property?: string;  // NEW - for house type
    weekOffDays?: string;  // NEW - comma-separated week-off days
    dueDate?: string;  // NEW - Due date field
  };
  onPreferenceChange?: (field: string, value: string) => void;
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;  // NEW - Edit mode flag
  customerInfo?: {  // NEW - Customer information for RUT/ROT validation
    accountType?: number;
    country?: string;
  };
}

interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: DropdownOption[];
  value: string;
  placeholder: string;
  onSelect: (value: string) => void;
  label: string;
  disabled?: boolean;
  tooltipContent?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  placeholder,
  onSelect,
  label,
  disabled = false,
  tooltipContent
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (optionValue: string) => {
    if (disabled) return;
    onSelect(optionValue);
    setIsOpen(false);
  };

  const selectedOption = options.find(option => option.value === value);

  return (
    <>
      <Form.Label className="preference-label">
        {label}
        {tooltipContent && (
          <Tooltip content={tooltipContent} position="top">
            <span className="info-icon">ⓘ</span>
          </Tooltip>
        )}
      </Form.Label>
      <div className="custom-dropdown" ref={dropdownRef}>
        <div 
          className={`dropdown-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className="dropdown-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <svg 
            className={`dropdown-arrow ${isOpen ? 'rotated' : ''}`}
            width="20" 
            height="20" 
            viewBox="0 0 20 20" 
            fill="none"
          >
            <path 
              d="M5 7.5L10 12.5L15 7.5" 
              stroke="currentColor" 
              strokeWidth="1.67" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </div>
        
        {isOpen && (
          <div className="dropdown-list">
            <div className="dropdown-content">
              {options.map((option) => (
                <div
                  key={option.value}
                  className={`list-item ${value === option.value ? 'selected' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  <div className="text-input">
                    <div className="paragraph-container">
                      <p className="option-text">{option.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

const InvoicePreferences: React.FC<InvoicePreferencesProps> = ({
  preferences,
  onPreferenceChange,
  copiedInvoiceData,
  isEditModeInvoice,
  customerInfo
}) => {
  const { t } = useTranslation();
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showVatModal, setShowVatModal] = useState(false);
  const [selectedVatForModal, setSelectedVatForModal] = useState<string>('25');
  
  // Debug: Log when preferences.vat changes
  useEffect(() => {
    console.log('🔍 VAT Debug - preferences.vat changed to:', preferences.vat);
  }, [preferences.vat]);
 
  // Update VAT when customer country changes (only if no copied data and VAT not already set)
  useEffect(() => {
    // Don't auto-set VAT if data has been populated from copiedInvoiceData
    if (customerInfo?.country && !copiedInvoiceData && !isDataPopulatedRef.current && (preferences.vat === undefined || preferences.vat === null)) {
      console.log('🔍 VAT Debug - Auto-setting VAT based on country');
      const newVAT = customerInfo.country === 'Sweden' ? '25' : '0';
      onPreferenceChange?.('vat', newVAT);
    }
  }, [customerInfo?.country, copiedInvoiceData, preferences.vat]);
  
  // Helper function to format ISO date to YYYY-MM-DD format
  const formatDateForInput = (dateStr: string | undefined): string => {
    if (!dateStr) return '';
    try {
      // Handle ISO format with time (2026-01-15T00:00:00)
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateStr);
      return '';
    }
  };
  
  // Track if data has been populated to prevent re-render
  const isDataPopulatedRef = React.useRef(false);
  
  // Initialize preferences from copiedInvoiceData when it changes
  useEffect(() => {
    console.log('🔍 VAT Debug - copiedInvoiceData effect running, isDataPopulatedRef:', isDataPopulatedRef.current);
    // Only populate if we have copiedInvoiceData and haven't already populated it
    if (copiedInvoiceData && !isDataPopulatedRef.current) {
      console.log('🔍 VAT Debug - Populating data from copiedInvoiceData');
      // Set currency if available
      if (copiedInvoiceData.currency) {
        onPreferenceChange?.('currency', copiedInvoiceData.currency);
      }
      
      // Set VAT if available
      if (copiedInvoiceData.vat !== undefined || copiedInvoiceData.vatPercent !== undefined) {
        const vatValue = copiedInvoiceData.vat ?? copiedInvoiceData.vatPercent;
        console.log('🔍 VAT Debug - copiedInvoiceData.vat:', copiedInvoiceData.vat);
        console.log('🔍 VAT Debug - copiedInvoiceData.vatPercent:', copiedInvoiceData.vatPercent);
        console.log('🔍 VAT Debug - vatValue being set:', vatValue);
        console.log('🔍 VAT Debug - Setting isDataPopulatedRef to true');
        onPreferenceChange?.('vat', vatValue.toString());
      }
      
      // Set due date if available
      if (copiedInvoiceData.dueDate) {
        const formattedDueDate = formatDateForInput(copiedInvoiceData.dueDate);
        onPreferenceChange?.('dueDate', formattedDueDate);
      } 
      
      // Set deduction type based on ROT/RUT
      if (copiedInvoiceData.rotDeduction || copiedInvoiceData.rootDeduction) {
        onPreferenceChange?.('deductionType', 'rot');
      } else if (copiedInvoiceData.rutDeduction) {
        onPreferenceChange?.('deductionType', 'rut');
      }
      
      // Set accommodation type if ROT is selected
      if (copiedInvoiceData.rotDeduction || copiedInvoiceData.rootDeduction) {
        if (copiedInvoiceData.house) {
          onPreferenceChange?.('accommodationType', 'house');
          if (copiedInvoiceData.property) {
            onPreferenceChange?.('property', copiedInvoiceData.property);
          }
        } else if (copiedInvoiceData.apartment) {
          onPreferenceChange?.('accommodationType', 'apartment');
          if (copiedInvoiceData.brfNo) {
            onPreferenceChange?.('brfOrgNumber', copiedInvoiceData.brfNo);
          }
          if (copiedInvoiceData.rotApartmentNo) {
            onPreferenceChange?.('apartmentHouseNo', copiedInvoiceData.rotApartmentNo);
          }
        }
      }
      
      // Mark as populated to prevent re-running
      isDataPopulatedRef.current = true;
     }
  }, [copiedInvoiceData]);
  
  // Reset the ref when copiedInvoiceData changes to a new object
  useEffect(() => {
    if (copiedInvoiceData) {
      isDataPopulatedRef.current = false;
    }
  }, [copiedInvoiceData?.invoiceId, copiedInvoiceData?.id]);
  
  // Currency options - matching InvoiceTool.js (comes from globalData.currencies)
  // All supported currencies in the system as per translation files and InvoiceTool.js
  const currencyOptions = [
    { value: 'SEK', label: 'SEK' },
    { value: 'USD', label: 'USD' },
    { value: 'EUR', label: 'EUR' },
    { value: 'GBP', label: 'GBP' },
    { value: 'CHF', label: 'CHF' },
    { value: 'CAD', label: 'CAD' },
    { value: 'AUD', label: 'AUD' },
    { value: 'NOK', label: 'NOK' },
    { value: 'DKK', label: 'DKK' },
    { value: 'JPY', label: 'JPY' },
    { value: 'CNY', label: 'CNY' },
  ];

  // VAT options - standard VAT rates
  const vatOptions = [
    { value: '0', label: '0%' },
    { value: '6', label: '6%' },
    { value: '12', label: '12%' },
    { value: '25', label: '25%' },
  ];

  // Show Dates options
  const showDatesOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];

  // RUT/ROT Deduction type options - matching item-details.component.tsx
  const deductionTypeOptions = [
    { value: 'rot', label: t('ROT_DEDUCTION') },
    { value: 'rut', label: t('RUT_DEDUCTION') },
    { value: 'none', label: t('NONE_REMOVE_RUT_ROT') }
  ];

  // Accommodation type options - removed 'other' option
  const accommodationTypeOptions = [
    { value: 'apartment', label: t('APARTMENT') },
    { value: 'house', label: t('HOUSE') },
  ];

  const handleOptionSelect = (field: string, value: string) => {
    // If deduction type is changing away from 'rot', reset accommodation fields
    if (field === 'deductionType' && value !== 'rot') {
      onPreferenceChange?.('accommodationType', '');
      onPreferenceChange?.('brfOrgNumber', '');
      onPreferenceChange?.('apartmentHouseNo', '');
      onPreferenceChange?.('property', '');
    }
    
    // If accommodation type is changing, reset specific fields
    if (field === 'accommodationType') {
      if (value === 'apartment') {
        // Reset house-specific field when switching to apartment
        onPreferenceChange?.('property', '');
      } else if (value === 'house') {
        // Reset apartment-specific fields when switching to house
        onPreferenceChange?.('brfOrgNumber', '');
        onPreferenceChange?.('apartmentHouseNo', '');
      }
    }
    // Show currency info modal when currency is changed and it's not SEK
    if (field === 'currency' && value !== 'SEK') {
      setShowCurrencyModal(true);
    }
    // Show VAT info modal when VAT is changed
    if (field === 'vat') {
      setSelectedVatForModal(value);
      setShowVatModal(true);
    }
    
    
    onPreferenceChange?.(field, value);
  };

  // Check if RUT/ROT deduction should be enabled
  // Enabled only when customer is Private (accountType === 1) and country is Sweden
  const isRutRotEnabled = 
    customerInfo?.accountType === 1 && 
    customerInfo?.country === 'Sweden';

  // DEBUG: Log RUT/ROT enablement status
  // Automatically reset deductionType to 'none' if conditions are not met
  React.useEffect(() => {
    if (!isRutRotEnabled && preferences.deductionType && preferences.deductionType !== 'none') {
      onPreferenceChange?.('deductionType', 'none');
    }
  }, [isRutRotEnabled, preferences.deductionType, onPreferenceChange]);

  // Show accommodation fields only when ROT deduction is selected
  const isROTSelected = preferences.deductionType === 'rot';
  
  // Check accommodation type - default to 'apartment' if not set
  const currentAccommodationType = preferences.accommodationType || 'apartment';
  const isApartmentSelected = currentAccommodationType === 'apartment';
  const isHouseSelected = currentAccommodationType === 'house';

  // Get default VAT based on customer country
  const getDefaultVAT = (): string => {
    console.log('🔍 VAT Debug - preferences.vat:', preferences.vat);
    console.log('🔍 VAT Debug - customerInfo?.country:', customerInfo?.country);
    const vatValue = preferences.vat !== undefined && preferences.vat !== null 
      ? preferences.vat.toString() 
      : (customerInfo?.country === 'Sweden' ? '25' : '0');
    console.log('🔍 VAT Debug - getDefaultVAT returning:', vatValue);
    return vatValue;
  };

  return (
    <>
      <VatInfoModal 
        show={showVatModal} 
        onHide={() => setShowVatModal(false)}
        selectedVat={selectedVatForModal}
      />
      <CurrencyInfoModal 
        show={showCurrencyModal} 
        onHide={() => setShowCurrencyModal(false)} 
      />
      <Card className="invoice-preferences-card">
        <Card.Header className="preferences-header">
          <Card.Title as="h3">{t('INVOICE_PREFERENCES')}</Card.Title>
        </Card.Header>
        <Card.Body className="preferences-body">
        {/* First Row - Main Preferences */}
        <Row className="preferences-row g-3 g-md-4">
          <Col xs={12} sm={6} md={6} lg={4} xl className="preference-field">
            <CustomDropdown
              options={currencyOptions}
              value={preferences.currency}
              placeholder="SEK"
              onSelect={(value) => handleOptionSelect('currency', value)}
              label={t('CURRENCY')}
              tooltipContent={t('CURRENCY_TOOLTIP')}
            />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4} xl className="preference-field">
            <CustomDropdown
              options={vatOptions}
              value={getDefaultVAT()}
              placeholder="25%"
              onSelect={(value) => handleOptionSelect('vat', value)}
              label={t('VAT')}
              tooltipContent={t('VAT_TOOLTIP')}
            />
          </Col>
          {isEditModeInvoice && (
          <Col xs={12} sm={6} md={6} lg={4} xl className="preference-field">
            <Form.Label className="preference-label">
              {t('DUE_DATE')}
              <Tooltip content={t('DUE_DATE') || 'Due Date'} position="top">
                <span className="info-icon">ⓘ</span>
              </Tooltip>
            </Form.Label>
            <Form.Control
              type="date"
              value={formatDateForInput(preferences.dueDate)}
              onChange={(e) => handleOptionSelect('dueDate', e.target.value)}
              className="input-value"
              disabled={false}
            />
          </Col>)}
            <Col xs={12} sm={6} md={6} lg={4} xl className="preference-field">
            <CustomDropdown
              options={showDatesOptions}
              value={preferences.showDates || 'yes'}
              placeholder="Yes"
              onSelect={(value) => handleOptionSelect('showDates', value)}
              label={t('SHOW_DATES')}
              tooltipContent={t('SHOW_DATES_TOOLTIP') || 'Show dates in invoice line items'}
            />
          </Col>
          <Col xs={12} sm={6} md={6} lg={4} xl className="preference-field">
            <CustomDropdown
              options={deductionTypeOptions}
              value={preferences.deductionType || 'none'}
              placeholder={t('NONE_REMOVE_RUT_ROT')}
              onSelect={(value) => handleOptionSelect('deductionType', value)}
              label={t('RUT_ROT_DEDUCTION_TYPE')}
              disabled={isEditModeInvoice || !isRutRotEnabled}
              tooltipContent={t('RUT_ROT_DEDUCTION_TYPE_TOOLTIP')}
            />
          </Col>
        
        </Row>

        {/* Second Row - Only for ROT Deduction */}
        {isROTSelected && (
          <Row className="preferences-row pt-3 g-3 g-md-4">
            <Col xs={12} sm={6} md={4} lg={4} xl className="preference-field">
              <CustomDropdown
                options={accommodationTypeOptions}
                value={currentAccommodationType}
                placeholder={t('APARTMENT')}
                onSelect={(value) => handleOptionSelect('accommodationType', value)}
                label={t('ACCOMMODATION_TYPE')}
                disabled={isEditModeInvoice}
              />
            </Col>
            
            {/* Apartment-specific fields - shown by default */}
            {isApartmentSelected && (
              <>
                <Col xs={12} sm={6} md={4} lg={4} xl className="preference-field">
                  <Form.Label className="preference-label">
                    {t('BRF_ORG_NUMBER')}
                    <Tooltip content={t('BRF_ORG_NUMBER') || 'BRF Organization Number'} position="top">
                      <span className="info-icon">ⓘ</span>
                    </Tooltip>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="200"
                    value={preferences.brfOrgNumber || ''}
                    onChange={(e) => handleOptionSelect('brfOrgNumber', e.target.value)}
                    className="input-value"
                    disabled={isEditModeInvoice}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={4} xl className="preference-field">
                  <Form.Label className="preference-label">
                    {t('APARTMENT_HOUSE_NO')}
                    <Tooltip content={t('APARTMENT_HOUSE_NO') || 'Apartment/House Number'} position="top">
                      <span className="info-icon">ⓘ</span>
                    </Tooltip>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="14"
                    value={preferences.apartmentHouseNo || ''}
                    onChange={(e) => handleOptionSelect('apartmentHouseNo', e.target.value)}
                    className="input-value"
                    disabled={isEditModeInvoice}
                  />
                </Col>
              </>
            )}
            
            {/* House-specific field */}
            {isHouseSelected && (
              <Col xs={12} sm={6} md={4} lg={4} xl className="preference-field">
                <Form.Label className="preference-label">
                  {t('PROPERTY')}
                  <Tooltip content={t('PROPERTY') || 'Property Details'} position="top">
                    <span className="info-icon">ⓘ</span>
                  </Tooltip>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder={t('ENTER_PROPERTY_DETAILS')}
                  value={preferences.property || ''}
                  onChange={(e) => handleOptionSelect('property', e.target.value)}
                  className="input-value"
                  disabled={isEditModeInvoice}
                />
              </Col>
            )}
          </Row>
        )}
      </Card.Body>
    </Card>
    </>
  );
};

export default InvoicePreferences;